import {
  CustomCheckbox,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomPopover,
  CustomSelect,
  CustomTextInput,
  Option,
  Text,
  useText,
} from '@src/components';
import { Button } from 'antd';
import { cardTypeIcon } from '@src/utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { monthsList, creditCardYears } from '@src/store/enums';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formCreditCardRegex } from '@src/utils/formRule';
import { getBankOfBinNumber, setPayWithLinkinEmail } from '@src/store/slice/mobilExpressSlice';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import iconInfo from '@src/assets/icons/icon-bilgi.svg';
import iconCircle from '@src/assets/icons/info-circle-white.svg';
import dayjs from '@src/utils/day';
import { prevStep } from '@src/store/slice/cardOrderSlice';
import iconBack from '@src/assets/icons/icon-back.svg';
import classNames from 'classnames';
import PropTypes from 'prop-types';

const NewCreditCards = ({ form, handleRegisteredCreditCard, counter, setCounter = () => {} }) => {
  const isMobil = useMediaQuery({ query: '(max-width: 991.98px)' });
  const { currentCardOrder } = useSelector((state) => state?.cardOrder);
  const [cardTypeImage, setCardTypeImage] = React.useState(false);
  const dispatch = useDispatch();
  const { storedCard, payWithLinkinEmail } = useSelector((state) => state?.mobilExpress);
  const [isStoreCard, setIsStoreCard] = useState(false);
  const [showPayLater, setShowPayLater] = useState(false);
  const [afterMount, setAfterMount] = useState(false);
  const isComplimentsVou = useMemo(() => currentCardOrder?.vouId === 94, [currentCardOrder]);
  const isMarketVou = useMemo(() => currentCardOrder?.vouId === 11, [currentCardOrder]);
  const mobilPadding = useMemo(() => {
    return isMobil ? { padding: '0 10px 0 0' } : null;
  }, [isMobil]);

  const nameOnCard = useText('nameOnCard');
  const enterYour16DigitCardNumber = useText('enterYour16DigitCardNumber');
  const monthText = useText('month2');
  const yearText = useText('year2');
  
  useEffect(() => {
    const today = dayjs().hour(23).minute(59).millisecond(0).second(59);
    const activationDate = dayjs(currentCardOrder?.cardActivationDate)
      .hour(23)
      .minute(59)
      .millisecond(0)
      .second(59);
    setShowPayLater(activationDate.isAfter(today));
    setAfterMount(activationDate.month() > today.month() || activationDate.year() > today.year());
  }, [currentCardOrder]);

  useEffect(() => {
    setCounter((count) => count + 1);
    return () => {
      setCounter((count) => count + 1);
    };
  }, []);

  const isValid = useMemo(() => {
    const values = form?.getFieldsValue();

    const cardNumber = values?.cardNumber?.replaceAll('_', '').replaceAll('-', '');
    const cardNumberLength = cardNumber?.length;
    const cardNumberIsValid = cardNumberLength === 16;

    const cardHolderName = values?.cardHolderName;
    const cardHolderNameIsValid = cardHolderName?.length > 0;

    const lastMonth = values?.lastMonth;
    const lastMonthIsValid = lastMonth > 0;

    const lastYear = values?.lastYear;
    const lastYearIsValid = lastYear?.length > 0;

    const cvv = values?.cvv?.replaceAll('_', '');
    const cvvIsValid = cvv?.length > 2;
    return (
      cardNumberIsValid &&
      cardHolderNameIsValid &&
      lastMonthIsValid &&
      lastYearIsValid &&
      cvvIsValid
    );
  }, [form, counter]);

  useEffect(() => {
    dispatch(setPayWithLinkinEmail(false));
  }, []);

  useEffect(() => {
    if (payWithLinkinEmail) {
      form.setFieldsValue({
        cardSaveChecked: false,
      });
    }
  }, [payWithLinkinEmail]);

  function clickSaveCardChecked(e) {
    setIsStoreCard(e.target.checked);
    dispatch(setPayWithLinkinEmail(false));
  }

  const cardNumberChange = useCallback(
    async (value) => {
      const cardNumber = value.replaceAll('_', '').replaceAll('-', '');
      if (cardNumber.length === 6) {
        const body = {
          bin: cardNumber,
        };
        const action = await dispatch(getBankOfBinNumber(body));
        if (getBankOfBinNumber.fulfilled.match(action)) {
          setCardTypeImage(cardTypeIcon(action.payload.data?.cardType));
        }
      }
    },
    [dispatch],
  );

  const updateEvent = useCallback(() => {
    setCounter((c) => c + 1);
  }, [counter]);

  const isVerifiedPayment = useCallback(async () => {
    if (afterMount && !isValid) {
      window.modalAction('payWithNewCard', true);
      return null;
    }
    window.handlePayment();
  }, [afterMount, isValid]);

  const handlePayLater = useCallback(async () => {
    if (!isValid) {
      window.modalAction('payWithNewCard', true);
      return null;
    }
    await window.payWhenBalanceActiveDay();
  }, [isValid]);

  const handlePayLaterCompliment = useCallback(async () => {
    try {
      if (!isValid) {
        window.modalAction('payWithNewCardForCompliment', true);
        return null;
      }
      await window.payWhenBalanceActiveDay();
    } catch (error) {
      console.error(error);
    }
  }, [isValid, form]);

  const getButtonText = () => {
    if (isComplimentsVou || isMarketVou) return <Text t="confirmPayment" />;
    if (afterMount) return <Text t="continue" />;
    return <Text t="payNow" />;
  };

  const renderPayLaterButton = () =>
    isComplimentsVou || isMarketVou ? (
      <button
        className={'compliments-payment-summary-later-pay'}
        style={{
          backgroundColor: '#162056',
          borderWidth: 1,
          marginBottom: 0,
          paddingTop: 14,
          paddingBottom: 14,
          paddingLeft: 4,
          paddingRight: 4,
          marginRight: 10,
        }}
        onClick={handlePayLaterCompliment}
      >
        <span>
          <Text t="payLater" />
        </span>
        <CustomPopover
          id="ticketRestaurant-cardOrders-payment-newCreditCards-popover"
          placement="topRight"
          title={false}
          content={
            <div
              style={{
                width: '300px',
                color: '#3f4957',
                padding: '7px 18px',
              }}
            >
              <span>
                <Text t="onTheNextScreen" />{' '}
                <b className="compliments-bold-text">
                  <Text t="automaticCollectionOnTheDayTheBalanceBecomes" />
                </b>{' '}
                <Text t="or" />{' '}
                <b className="compliments-bold-text">
                  <Text t="paymentViaTheLinkSentToYourE" />
                </b>{' '}
                <Text t="youCanChooseOneOfTheMethods" />
              </span>
            </div>
          }
          trigger="hover"
          arrowPointAtCenter={true}
        >
          <div
            style={{
              marginRight: 0,
              marginBottom: 2,
            }}
          >
            <CustomImage src={iconCircle} />
          </div>
        </CustomPopover>
      </button>
    ) : (
      <button
        className={classNames({
          'pay-when-balance-active-day-button': true,
        })}
        style={{ backgroundColor: 'transparent', borderWidth: 0 }}
        onClick={handlePayLater}
      >
        <span>
          <Text t="payLater" />
        </span>
        <CustomPopover
          id="ticketRestaurant-cardOrders-payment-newCreditCards-popover"
          placement="topRight"
          title={false}
          content={
            <div
              style={{
                width: '300px',
                color: '#3f4957',
                padding: '7px 18px',
              }}
            >
              <Text t="ifYouSelectThisOptionTheTotalBalance" />
            </div>
          }
          trigger="hover"
          arrowPointAtCenter={true}
        >
          <div
            style={{
              marginRight: '24px',
              marginBottom: '7px',
            }}
          >
            <CustomImage src={iconInfo} />
          </div>
        </CustomPopover>
      </button>
    );

  return (
    <>
      <div className="saved-credit-cards-card new-credit-card-page">
        <ParentElement
          storedCard={storedCard}
          handleRegisteredCreditCard={handleRegisteredCreditCard}
        >
          <div className="new-credit-card-content">
            <CustomForm
              name="creditCardForm"
              className="credit-card-form"
              initialValues={{
                cardSaveChecked: false,
              }}
              autoComplete="off"
              layout={'vertical'}
              form={form}
            >
              <div className="row">
                <div className="col-lg-6">
                  <CustomFormItem
                    label={<Text t="nameOnCard" />}
                    name="cardHolderName"
                    rules={[{ required: true, message: <Text t="pleaseEnterNameOnCard" /> }]}
                  >
                    <CustomTextInput
                      id="ticketRestaurant-cardOrders-payment-newCreditCards-cardHolderName-textInput"
                      onInput={updateEvent}
                      placeholder={nameOnCard}
                    />
                  </CustomFormItem>
                </div>
                <div className="col-lg-6">
                  <CustomFormItem
                    label={<Text t="cardNumber2" />}
                    name="cardNumber"
                    className="card-number-form-item"
                    rules={[
                      { required: true, message: <Text t="pleaseEnterCardNumber" /> },
                      {
                        validator: formCreditCardRegex,
                        message: <Text t="pleaseEnterAValidCardNumber" />,
                      },
                      {
                        validator: async (field, value) => {
                          setCounter((count) => count + 1);
                          return Promise.resolve();
                        },
                        message: '',
                      },
                    ]}
                  >
                    <CardInput
                      id="ticketRestaurant-cardOrders-payment-newCreditCards-cardNumber-cardInput"
                      cardTypeImage={cardTypeImage}
                      cardNumberChange={cardNumberChange}
                      enterYour16DigitCardNumber={enterYour16DigitCardNumber}
                    />
                  </CustomFormItem>
                </div>
              </div>

              <div className="row">
                <div className="col-lg-6">
                  <CustomFormItem
                    name={'expireMonthYear'}
                    className="expire-form-item"
                    label={
                      <span>
                        <Text t="expirationDate" />
                        <span className="form-required-mark">*</span>
                      </span>
                    }
                  >
                    <div className="row">
                      <div className="ant-col-xs-12" style={mobilPadding}>
                        <CustomFormItem
                          label={false}
                          name="lastMonth"
                          rules={[
                            { required: true, message: <Text t="requiredField2" /> },
                            {
                              validator: async (field, value) => {
                                setCounter((count) => count + 1);
                                return Promise.resolve();
                              },
                              message: '',
                            },
                          ]}
                        >
                          <CustomSelect
                            id="ticketRestaurant-cardOrders-payment-newCreditCards-lastMonth-select"
                            placeholder={monthText}
                          >
                            {monthsList.map((month) => (
                              <Option
                                id={`ticketRestaurant-cardOrders-payment-newCreditCards-lastMonth-${month?.value}-option`}
                                key={month.value}
                                value={month.value}
                              >
                                {month.value}
                              </Option>
                            ))}
                          </CustomSelect>
                        </CustomFormItem>
                      </div>

                      <div className="ant-col-xs-12" style={mobilPadding}>
                        <CustomFormItem
                          label={false}
                          name="lastYear"
                          rules={[
                            { required: true, message: <Text t="requiredField2" /> },
                            {
                              validator: async (field, value) => {
                                setCounter((count) => count + 1);
                                return Promise.resolve();
                              },
                              message: '',
                            },
                          ]}
                        >
                          <CustomSelect
                            id="ticketRestaurant-cardOrders-payment-newCreditCards-lastYear-select"
                            placeholder={yearText}
                          >
                            {creditCardYears.map((year) => (
                              <Option
                                id={`ticketRestaurant-cardOrders-payment-newCreditCards-lastYear-${year.value}-option`}
                                key={year.value}
                                value={year.value}
                              >
                                {year.value}
                              </Option>
                            ))}
                          </CustomSelect>
                        </CustomFormItem>
                      </div>
                    </div>
                  </CustomFormItem>
                </div>
                <div className="col-lg-6">
                  <CustomFormItem
                    label={
                      <span>
                        <Text t="securityCode" />
                        <span className="form-required-mark">*</span>{' '}
                      </span>
                    }
                    name="cvv"
                    className="cvv-form-item"
                    rules={[
                      {
                        validator: async (field, val) => {
                          const value = val?.replace('_', '').split('');
                          try {
                            if (value.length === 3) {
                              setCounter((count) => count + 1);
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error());
                          } catch (e) {
                            return Promise.reject(new Error());
                          }
                        },
                        message: <Text t="pleaseEnterTheSecurityCodeOnTheCard" />,
                      },
                    ]}
                  >
                    <CustomMaskInput
                      id="ticketRestaurant-cardOrders-payment-newCreditCards-cvv-maskInput"
                      mask={'999'}
                    >
                      <CustomInput
                        id="ticketRestaurant-cardOrders-payment-newCreditCards-cvv-input"
                        placeholder="CVC / CVV"
                      />
                    </CustomMaskInput>
                  </CustomFormItem>
                </div>
              </div>

              <div className="info-text-contain">
                <div className="col-md-12">
                  <div
                    style={{
                      fontFamily: 'UbuntuRegular, sans-serif',
                      fontSize: '14px',
                      fontWeight: 'normal',
                      fontStretch: 'normal',
                      fontStyle: 'normal',
                      lineHeight: 'normal',
                      letterSpacing: 'normal',
                      textAlign: 'left',
                      color: '#1d2228',
                    }}
                  >
                    <Text t="youCanSaveYourCardInformationToComplete2" />
                  </div>
                </div>
                <br />
                <div className="col-md-12">
                  <CustomFormItem label={false} name="cardSaveChecked" valuePropName="checked">
                    <CustomCheckbox
                      id="ticketRestaurant-cardOrders-payment-newCreditCards-cardSaveChecked-checkbox"
                      value={isStoreCard}
                      onChange={clickSaveCardChecked}
                    >
                      <span className="info-text-title">
                        <Text t="iWantToSecurelySaveMyCardInformation" />
                      </span>
                    </CustomCheckbox>
                  </CustomFormItem>
                </div>
              </div>
            </CustomForm>
          </div>
        </ParentElement>
      </div>

      <div className={'pay-buttons-where-under-page'}>
        <div className="step-btn-container">
          <Button
            id="ticketRestaurant-cardOrders-payment-newCreditCards-back-btn"
            className="back-btn"
            onClick={() => dispatch(prevStep())}
          >
            <CustomImage src={iconBack} />
            <Text t="turnBack" />
          </Button>
        </div>
        {showPayLater && !afterMount && <>{renderPayLaterButton()}</>}
        <Button
          id="ticketRestaurant-cardOrders-payment-newCreditCards-payment-btn"
          disabled={afterMount ? false : !isValid}
          onClick={isVerifiedPayment}
          className="payment-btn"
        >
          {getButtonText()}
        </Button>
      </div>
    </>
  );
};

NewCreditCards.propTypes = {
  form: PropTypes.any,
};

export default NewCreditCards;

const CardInput = ({ cardTypeImage, cardNumberChange, onChange, enterYour16DigitCardNumber, ...props }) => {
  const change = (e) => {
    cardNumberChange?.(e.target.value);
    onChange?.(e);
  };

  return (
    <CustomMaskInput
      id="ticketRestaurant-cardOrders-payment-newCreditCards-bankCard-maskInput"
      {...props}
      mask={'9999-9999-9999-9999'}
      onChange={change}
    >
      <CustomInput
        id="ticketRestaurant-cardOrders-payment-newCreditCards-bankCard-input"
        placeholder={enterYour16DigitCardNumber}
        suffix={cardTypeImage ? <CustomImage src={cardTypeImage} /> : <span />}
      />
    </CustomMaskInput>
  );
};

CardInput.propTypes = {
  cardTypeImage: PropTypes.string,
  cardNumberChange: PropTypes.func,
  onChange: PropTypes.func,
  enterYour16DigitCardNumber: PropTypes.string,
};

const ParentElement = ({ children, storedCard, handleRegisteredCreditCard }) => {
  return (
    <CustomCollapseCard
      headerRightInfo={
        storedCard.length > 0 && (
          <div className="header-btn" onClick={handleRegisteredCreditCard}>
            <Text t="iWantToPayWithMyRegisteredCredit" />
          </div>
        )
      }
      cardTitle={
        <div className="title">
          <Text t="savedCreditCardsTitle" />
        </div>
      }
    >
      <div className="custom-card-body">{children}</div>
    </CustomCollapseCard>
  );
};
